<template>
  <div class="list-container">
    <div class="d-flex align-items-center justify-content-between flex-wrap gap-2">
      <h1 class="mb-0">IES4一括取込</h1>
      <!-- 操作説明はPDFで配布しており、画面からも開けるようにしている -->
      <a class="manual-link" :href="manualUrl" target="_blank" rel="noopener">操作説明（PDF）</a>
    </div>
    <p class="text-muted small">
      過去分のIES4ファイルをまとめて取り込みます。システム管理者専用です。
      解析だけでは報告書は作られません。登録は既定で仮登録です。
    </p>

    <div v-if="errorMessages.length" class="alert alert-danger">
      <div class="fw-bold mb-1">エラー</div>
      <ul class="mb-0">
        <li v-for="(message, index) in errorMessages" :key="index">{{ message }}</li>
      </ul>
    </div>
    <div v-if="successMessage" class="alert alert-success">{{ successMessage }}</div>

    <!-- STEP 1: 投入 -->
    <div class="card my-3">
      <div class="card-header fw-bold">STEP 1 ファイルを投入して照合する</div>
      <div class="card-body">
        <div
          class="drop-area"
          :class="{ 'drop-area-active': isDragOver, 'drop-area-disabled': isBusy }"
          @dragover.prevent="handleDragOver"
          @dragleave.prevent="handleDragLeave"
          @drop.prevent="handleDrop"
        >
          <p class="mb-2">IES4ファイルをまとめてドラッグ＆ドロップ、またはファイルを選択してください（複数選択可）</p>
          <input
            id="ies4BulkFiles"
            ref="fileInputRef"
            type="file"
            class="form-control"
            accept=".IES4,.ies4"
            multiple
            :disabled="isBusy"
            @change="handleFileChange"
          >
          <div class="form-text">
            1ファイルあたり10MBまで。件数の上限はありません（{{ analyzeChunkSize }}件ずつに分けて送ります）。
            既に取り込み済みのファイルは自動でスキップされます。
          </div>
        </div>

        <div v-if="session" class="form-check mt-3">
          <input id="appendToSession" class="form-check-input" type="checkbox" v-model="appendToSession" :disabled="isBusy">
          <label class="form-check-label" for="appendToSession">
            表示中のセッション（No.{{ session.id }}）に追加する
          </label>
        </div>
      </div>
    </div>

    <template v-if="session">
      <!-- 照合サマリ -->
      <div class="card my-3">
        <div class="card-header fw-bold d-flex justify-content-between align-items-center">
          <span>照合結果（セッション No.{{ session.id }}）</span>
          <span class="badge" :class="statusBadgeClass">{{ statusLabel }}</span>
        </div>
        <div class="card-body">
          <div class="summary-grid">
            <div class="summary-cell">
              <span class="summary-label">投入</span>
              <span class="summary-value">{{ session.total_file_count }}</span>
            </div>
            <div class="summary-cell ok">
              <span class="summary-label">自動確定</span>
              <span class="summary-value">{{ session.matched_count }}</span>
            </div>
            <div class="summary-cell warn">
              <span class="summary-label">要確認</span>
              <span class="summary-value">{{ session.needs_review_count }}</span>
            </div>
            <div class="summary-cell stop">
              <span class="summary-label">未マッチ</span>
              <span class="summary-value">{{ session.unmatched_count }}</span>
            </div>
            <div class="summary-cell">
              <span class="summary-label">取込済み</span>
              <span class="summary-value">{{ session.already_imported_count }}</span>
            </div>
            <div class="summary-cell stop">
              <span class="summary-label">解析エラー</span>
              <span class="summary-value">{{ session.error_count }}</span>
            </div>
          </div>

          <div v-if="session.committed_count || session.failed_count" class="mt-3">
            <span class="badge text-bg-success me-2">登録成功 {{ session.committed_count }}</span>
            <span class="badge text-bg-danger">登録失敗 {{ session.failed_count }}</span>
          </div>
        </div>
      </div>

      <!-- STEP 2: マッピングCSV -->
      <div class="card my-3">
        <div class="card-header fw-bold">STEP 2 要確認・未マッチをCSVで割り当てる</div>
        <div class="card-body">
          <p class="text-muted small mb-3">
            CSVをダウンロードし、<span class="fw-bold">「建物ID」列</span>を埋めて戻してください。
            候補が3件まで併記されているので、多くはコピペで済みます。
            割り当てた内容は辞書として保存され、次回以降は自動で確定します。
          </p>
          <div class="d-flex flex-wrap gap-2 align-items-center">
            <button type="button" class="btn btn-outline-primary" :disabled="isBusy || !needsHumanInput" @click="downloadMappingCsv(false)">
              要確認・未マッチのCSVをダウンロード（{{ needsHumanInputCount }}件）
            </button>
            <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="isBusy" @click="downloadMappingCsv(true)">
              全明細のCSV
            </button>
          </div>

          <hr>

          <label for="mappingCsv" class="form-label fw-bold">埋めたCSVを取り込む</label>
          <div class="d-flex flex-wrap gap-2 align-items-center">
            <input id="mappingCsv" ref="csvInputRef" type="file" class="form-control" accept=".csv" :disabled="isBusy" style="max-width: 420px;">
            <button type="button" class="btn btn-primary" :disabled="isBusy" @click="applyMapping">反映する</button>
          </div>
          <div class="form-text">Excelで編集した場合は「CSV UTF-8（コンマ区切り）」で保存してください。</div>
        </div>
      </div>

      <!-- STEP 3: 登録 -->
      <div class="card my-3">
        <div class="card-header fw-bold">STEP 3 確定した分を登録する</div>
        <div class="card-body">
          <p class="mb-3">
            建物が確定している <span class="fw-bold">{{ session.matched_count }}件</span> を登録します。
            要確認・未マッチ・取込済み・解析エラーの明細は登録されません。
          </p>
          <div class="d-flex flex-wrap gap-3 align-items-center">
            <span class="fw-bold">登録方法</span>
            <div class="form-check">
              <input id="bulkBookRegistFlg0" class="form-check-input" type="radio" :value="0" v-model="bookRegistFlg" :disabled="isBusy">
              <label class="form-check-label" for="bulkBookRegistFlg0">仮登録（推奨）</label>
            </div>
            <div class="form-check">
              <input id="bulkBookRegistFlg1" class="form-check-input" type="radio" :value="1" v-model="bookRegistFlg" :disabled="isBusy">
              <label class="form-check-label" for="bulkBookRegistFlg1">本登録</label>
            </div>
            <button type="button" class="btn btn-success" :disabled="isBusy || !session.matched_count" @click="commit">
              {{ status === 'committing' ? '登録中...' : `${session.matched_count}件を登録` }}
            </button>
          </div>
        </div>
      </div>

      <!-- 明細 -->
      <div class="card my-3">
        <div class="card-header fw-bold d-flex justify-content-between align-items-center">
          <span>明細（{{ filteredEntries.length }}件）</span>
          <select class="form-select form-select-sm" style="width: auto;" v-model="entryFilter">
            <option value="all">すべて</option>
            <option value="matched">自動確定のみ</option>
            <option value="needs_review">要確認のみ</option>
            <option value="unmatched">未マッチのみ</option>
            <option value="already_imported">取込済みのみ</option>
            <option value="parse_error">解析エラーのみ</option>
          </select>
        </div>
        <div class="card-body table-responsive">
          <table class="table table-bordered table-sm align-middle mb-0">
            <thead class="table-dark">
              <tr>
                <th class="text-nowrap">判定</th>
                <th class="text-nowrap">ファイル名</th>
                <th class="text-nowrap">IES4建物名</th>
                <th class="text-nowrap">測定日</th>
                <th class="text-nowrap">突合先の建物</th>
                <th class="text-nowrap">根拠</th>
                <th>メッセージ</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="entry in filteredEntries" :key="entry.id">
                <td class="text-nowrap">
                  <span class="badge" :class="matchBadgeClass(entry.match_status)">{{ matchLabel(entry.match_status) }}</span>
                  <span v-if="entry.commit_status === 'committed'" class="badge text-bg-success ms-1">登録済</span>
                  <span v-else-if="entry.commit_status === 'failed'" class="badge text-bg-danger ms-1">登録失敗</span>
                </td>
                <td class="text-nowrap small">{{ entry.source_file_name }}</td>
                <td class="text-nowrap small">
                  {{ entry.source_building_name || '-' }}
                  <span v-if="entry.building_number" class="text-muted">（No.{{ entry.building_number }}）</span>
                </td>
                <td class="text-nowrap small">{{ entry.measure_dt || '-' }}</td>
                <td class="small">
                  <template v-if="entry.building_nm">
                    {{ entry.building_nm }}
                    <span class="text-muted d-block">{{ entry.customer_nm }}</span>
                  </template>
                  <template v-else-if="entry.candidates && entry.candidates.length">
                    <span class="text-muted">候補：</span>
                    <span v-for="candidate in entry.candidates" :key="candidate.m_building_id" class="d-block">
                      ID {{ candidate.m_building_id }}／{{ candidate.building_nm }}
                      <span class="text-muted">（{{ candidate.customer_nm }}／{{ candidate.score }}）</span>
                    </span>
                  </template>
                  <span v-else class="text-muted">-</span>
                </td>
                <td class="text-nowrap small">{{ ruleLabel(entry.match_rule) }}</td>
                <td class="small">
                  <div v-for="(message, index) in entry.messages" :key="index">{{ message.message }}</div>
                  <router-link v-if="entry.t_measurement_report_id" :to="`/measurement_reports/${entry.t_measurement_report_id}`">
                    報告書ID {{ entry.t_measurement_report_id }} を開く
                  </router-link>
                </td>
              </tr>
              <tr v-if="!filteredEntries.length">
                <td colspan="7" class="text-center text-muted">該当する明細はありません</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>

    <div v-if="isBusy" class="loading-overlay">
      <div class="text-center">
        <div class="spinner-border text-light" role="status"></div>
        <div v-if="progress" class="text-light mt-2">
          {{ progress.label }}中 {{ progress.done }} / {{ progress.total }} 件
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref, watchEffect } from 'vue';
import {
  analyzeMeasurementReportIes4Bulk,
  applyMeasurementReportIes4BulkMapping,
  commitMeasurementReportIes4Bulk,
  downloadMeasurementReportIes4BulkMappingCsv
} from '@/api/axios';
import { IES4_BULK_IMPORT_MANUAL_URL } from '@/utils/manualLinks';

const MAX_UPLOAD_SIZE = 10 * 1024 * 1024;
// 何十件も1リクエストで送ると、Webサーバのリクエストサイズ上限や
// ロードバランサのタイムアウトに当たる。少数ずつに分けて順に送る。
const ANALYZE_CHUNK_SIZE = 10;
const COMMIT_CHUNK_SIZE = 10;

const chunk = (items, size) => {
  const chunks = [];
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }
  return chunks;
};

const MATCH_LABELS = {
  matched: '自動確定',
  needs_review: '要確認',
  unmatched: '未マッチ',
  already_imported: '取込済み',
  parse_error: '解析エラー',
  pending: '未判定'
};

const RULE_LABELS = {
  alias: '辞書',
  name_exact: '建物名一致',
  address_exact: '住所一致',
  similarity: '類似度',
  manual: '手動割当'
};

export default {
  setup() {
    watchEffect(() => {
      document.title = 'IES4一括取込';
    });

    const fileInputRef = ref(null);
    const csvInputRef = ref(null);
    const session = ref(null);
    const entries = ref([]);
    const errorMessages = ref([]);
    const successMessage = ref('');
    const isDragOver = ref(false);
    const appendToSession = ref(false);
    const bookRegistFlg = ref(0);
    const entryFilter = ref('all');
    // idle / analyzing / applying / committing
    const status = ref('idle');
    // 分割送信の進捗（例: 20/75）
    const progress = ref(null);

    const isBusy = computed(() => status.value !== 'idle');

    const filteredEntries = computed(() => (
      entryFilter.value === 'all'
        ? entries.value
        : entries.value.filter((entry) => entry.match_status === entryFilter.value)
    ));

    const needsHumanInputCount = computed(() => (
      (session.value?.needs_review_count || 0) + (session.value?.unmatched_count || 0)
    ));
    const needsHumanInput = computed(() => needsHumanInputCount.value > 0);

    const statusLabel = computed(() => ({
      analyzing: '解析中',
      analyzed: '照合済み',
      committing: '登録中',
      completed: '登録完了',
      failed: '失敗'
    }[session.value?.status] || session.value?.status || ''));

    const statusBadgeClass = computed(() => ({
      completed: 'text-bg-success',
      failed: 'text-bg-danger',
      committing: 'text-bg-warning'
    }[session.value?.status] || 'text-bg-secondary'));

    const matchLabel = (value) => MATCH_LABELS[value] || value;
    const ruleLabel = (value) => (value ? (RULE_LABELS[value] || value) : '-');
    const matchBadgeClass = (value) => ({
      matched: 'text-bg-success',
      needs_review: 'text-bg-warning',
      unmatched: 'text-bg-danger',
      already_imported: 'text-bg-secondary',
      parse_error: 'text-bg-danger'
    }[value] || 'text-bg-secondary');

    const extractErrorMessages = (error) => {
      const list = error?.response?.data?.errors;
      if (Array.isArray(list) && list.length) return list.map((item) => item.message || String(item));
      return ['処理に失敗しました'];
    };

    const applyResponse = (data) => {
      session.value = data.session;
      entries.value = data.entries || [];
    };

    const validateFiles = (files) => {
      const invalid = files.find((file) => !file.name.toLowerCase().endsWith('.ies4'));
      if (invalid) return `拡張子がIES4のファイルを選択してください: ${invalid.name}`;

      const tooLarge = files.find((file) => file.size > MAX_UPLOAD_SIZE);
      if (tooLarge) return `ファイルサイズは10MB以下にしてください: ${tooLarge.name}`;

      return '';
    };

    const analyze = async (files) => {
      errorMessages.value = [];
      successMessage.value = '';
      if (!files.length) return;

      const validationError = validateFiles(files);
      if (validationError) {
        errorMessages.value = [validationError];
        return;
      }

      status.value = 'analyzing';
      const chunks = chunk(files, ANALYZE_CHUNK_SIZE);
      let sessionId = appendToSession.value ? session.value?.id : null;
      let analyzed = 0;

      try {
        for (const files_of_chunk of chunks) {
          progress.value = { done: analyzed, total: files.length, label: '解析' };
          const response = await analyzeMeasurementReportIes4Bulk({ files: files_of_chunk, sessionId });
          applyResponse(response.data);
          // 2回目以降は同じセッションへ追加する
          sessionId = response.data.session.id;
          analyzed += files_of_chunk.length;
        }
        successMessage.value = `${analyzed}件を解析しました`;
      } catch (error) {
        errorMessages.value = extractErrorMessages(error);
        if (analyzed > 0) {
          errorMessages.value.push(`${analyzed}件までは解析済みです。残りは「表示中のセッションに追加する」を選んで投入し直してください。`);
        }
      } finally {
        progress.value = null;
        status.value = 'idle';
        if (fileInputRef.value) fileInputRef.value.value = '';
      }
    };

    const handleFileChange = async (event) => {
      await analyze(Array.from(event.target.files || []));
    };

    const handleDragOver = () => {
      if (!isBusy.value) isDragOver.value = true;
    };
    const handleDragLeave = () => { isDragOver.value = false; };
    const handleDrop = async (event) => {
      isDragOver.value = false;
      if (isBusy.value) return;
      await analyze(Array.from(event.dataTransfer?.files || []));
    };

    const downloadMappingCsv = async (all) => {
      errorMessages.value = [];
      try {
        const response = await downloadMeasurementReportIes4BulkMappingCsv(session.value.id, { all });
        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
        const link = document.createElement('a');
        link.href = url;
        link.download = `ies4_mapping_${session.value.id}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        errorMessages.value = ['マッピングCSVのダウンロードに失敗しました'];
      }
    };

    const applyMapping = async () => {
      errorMessages.value = [];
      successMessage.value = '';

      const file = csvInputRef.value?.files?.[0];
      if (!file) {
        errorMessages.value = ['マッピングCSVを選択してください'];
        return;
      }

      status.value = 'applying';
      try {
        const response = await applyMeasurementReportIes4BulkMapping(session.value.id, file);
        applyResponse(response.data);
        successMessage.value = `${response.data.applied_count}件を割り当てました`;
      } catch (error) {
        errorMessages.value = extractErrorMessages(error);
      } finally {
        status.value = 'idle';
        if (csvInputRef.value) csvInputRef.value.value = '';
      }
    };

    const commit = async () => {
      errorMessages.value = [];
      successMessage.value = '';
      status.value = 'committing';

      const total = session.value.matched_count;
      let afterEntryId = null;
      let processed = 0;

      try {
        // 残りが無くなるまで少数ずつ登録する。途中で失敗しても、
        // ここまでに登録した分は明細に「登録済」として残る。
        for (;;) {
          progress.value = { done: processed, total, label: '登録' };
          const response = await commitMeasurementReportIes4Bulk(session.value.id, {
            bookRegistFlg: bookRegistFlg.value,
            limit: COMMIT_CHUNK_SIZE,
            afterEntryId
          });
          applyResponse(response.data);

          processed += response.data.processed_count || 0;
          afterEntryId = response.data.last_entry_id || afterEntryId;
          if (!response.data.processed_count || !response.data.remaining_count) break;
        }

        successMessage.value = `登録が完了しました（成功 ${session.value.committed_count}件／失敗 ${session.value.failed_count}件）`;
      } catch (error) {
        errorMessages.value = extractErrorMessages(error);
        if (processed > 0) {
          errorMessages.value.push(`${processed}件までは登録済みです。もう一度「登録」を押すと続きから再開します。`);
        }
      } finally {
        progress.value = null;
        status.value = 'idle';
      }
    };

    return {
      manualUrl: IES4_BULK_IMPORT_MANUAL_URL,
      analyzeChunkSize: ANALYZE_CHUNK_SIZE,
      fileInputRef,
      csvInputRef,
      session,
      entries,
      filteredEntries,
      entryFilter,
      errorMessages,
      successMessage,
      isDragOver,
      appendToSession,
      bookRegistFlg,
      status,
      progress,
      isBusy,
      needsHumanInput,
      needsHumanInputCount,
      statusLabel,
      statusBadgeClass,
      matchLabel,
      ruleLabel,
      matchBadgeClass,
      handleFileChange,
      handleDragOver,
      handleDragLeave,
      handleDrop,
      downloadMappingCsv,
      applyMapping,
      commit
    };
  }
};
</script>

<style scoped>
/* 操作説明PDFへのリンク。画面の主導線ではないため控えめに見せる */
.manual-link {
  font-size: 0.9rem;
  color: #2f4e6d;
  text-decoration: underline;
  white-space: nowrap;
}

.manual-link:hover {
  color: #1b2f42;
}

.drop-area {
  border: 2px dashed #ced4da;
  border-radius: 6px;
  padding: 20px;
}
.drop-area-active {
  border-color: #0d6efd;
  background-color: #f1f7ff;
}
.drop-area-disabled {
  opacity: 0.6;
}
.summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 10px;
}
.summary-cell {
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.summary-cell.ok { border-left: 4px solid #198754; }
.summary-cell.warn { border-left: 4px solid #ffc107; }
.summary-cell.stop { border-left: 4px solid #dc3545; }
.summary-label {
  font-size: 12px;
  color: #6c757d;
}
.summary-value {
  font-size: 22px;
  font-weight: 700;
}
.loading-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1080;
}
</style>
